import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import {
  FaArrowLeft, FaSpinner, FaTruck, FaEnvelope, FaPhone, FaMapMarkerAlt,
  FaFileInvoiceDollar,
} from 'react-icons/fa'
import { Card, Spinner, Banner, PageHeader, StatusBadge, Toast, useToast } from '../components/ui'
import Thumb from '../components/Thumb'
import { money, formatDateTime, statusLabel } from '../lib/format'
import { SHOW_STOCK } from '../lib/features'
import * as api from '../lib/api'

/**
 * One order, everything about it, and the controls to move it along.
 *
 * Status changes go through set_order_status(), not a plain update, because the
 * transition has consequences: cancelling returns the stock to the shelf and
 * un-cancelling takes it off again. Doing that in the browser as two calls would
 * leave stock wrong whenever the second one failed.
 */

const NEXT_ACTIONS = {
  pending: [['paid', 'Mark as paid']],
  paid: [['processing', 'Start packing']],
  processing: [['shipped', 'Mark as shipped']],
  shipped: [['delivered', 'Mark as delivered']],
  delivered: [],
  cancelled: [['paid', 'Reinstate this order']],
  refunded: [],
}

const OrderDetailPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [order, setOrder] = useState(null)
  const [state, setState] = useState({ loading: true, error: null })
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState('')
  const [tracking, setTracking] = useState('')
  const [invoice, setInvoice] = useState(null)
  const { toast, notify, notifyError, dismiss } = useToast()

  const load = async () => {
    setState({ loading: true, error: null })
    try {
      const row = await api.getOrder(id)
      setOrder(row)
      setTracking(row?.tracking_number ?? '')
      setState({ loading: false, error: null })
    } catch (err) {
      setState({ loading: false, error: err })
    }
  }

  useEffect(() => {
    load()
    // Asked separately and allowed to fail quietly: whether this order has been
    // invoiced changes a button's wording, and an order page that will not open
    // because of it would be a poor trade.
    api.getOrderInvoice(id).then(setInvoice).catch(() => setInvoice(null))
  }, [id])

  /**
   * Raise an invoice from this order, or open the one already raised.
   *
   * invoice_from_order() is idempotent, so a double click cannot produce two
   * invoices for one sale — which is the mistake this button would otherwise
   * make easy, and an expensive one to explain to a customer.
   */
  const makeInvoice = async () => {
    setBusy(true)
    try {
      const document_ = await api.invoiceFromOrder(id)
      navigate(`/invoices/${document_.id}`)
    } catch (err) {
      notifyError(err)
      setBusy(false)
    }
  }

  const changeStatus = async (next) => {
    setBusy(true)
    try {
      await api.setOrderStatus(id, next, note.trim() || null)
      notify(`Order marked as ${statusLabel(next).toLowerCase()}.`)
      setNote('')
      load()
    } catch (err) {
      notifyError(err)
    } finally {
      setBusy(false)
    }
  }

  const saveTracking = async () => {
    setBusy(true)
    try {
      await api.updateOrder(id, { tracking_number: tracking.trim() || null })
      notify('Tracking number saved.')
      load()
    } catch (err) {
      notifyError(err)
    } finally {
      setBusy(false)
    }
  }

  if (state.loading) return <Spinner label="Loading the order…" />
  if (state.error) {
    return <Banner tone="error"><strong>Could not load the order.</strong> {state.error.message}</Banner>
  }
  if (!order) return <Banner tone="warning">That order could not be found.</Banner>

  const actions = NEXT_ACTIONS[order.status] ?? []
  const canCancel = !['cancelled', 'refunded', 'delivered'].includes(order.status)

  return (
    <div>
      <PageHeader title={order.order_number} subtitle={`Placed ${formatDateTime(order.placed_at)}`}>
        <Link to="/orders" className="btn-ghost">
          <FaArrowLeft className="text-[11px]" />
          All orders
        </Link>
        {invoice ? (
          <Link to={`/invoices/${invoice.id}`} className="btn-ghost">
            <FaFileInvoiceDollar className="text-[11px]" />
            {invoice.number}
          </Link>
        ) : (
          <button onClick={makeInvoice} disabled={busy} className="btn-ghost">
            {busy ? <FaSpinner className="animate-spin" /> : <FaFileInvoiceDollar className="text-[11px]" />}
            Make an invoice
          </button>
        )}
      </PageHeader>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card title="Items" bodyClassName="">
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Size / colour</th>
                    <th className="num">Price</th>
                    <th className="num">Qty</th>
                    <th className="num">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(order.items ?? []).map((item) => (
                    <tr key={item.id}>
                      <td>
                        <div className="flex items-center gap-2.5">
                          <Thumb src={item.image} alt={item.name} />
                          <div className="min-w-0">
                            <div className="max-w-[220px] truncate font-medium text-ink">{item.name}</div>
                            <div className="text-2xs text-ink-muted">{item.sku}</div>
                          </div>
                        </div>
                      </td>
                      <td className="text-2xs text-ink-secondary">
                        {[item.size, item.color].filter(Boolean).join(' · ') || '—'}
                      </td>
                      <td className="num">{money(item.unit_price)}</td>
                      <td className="num">{item.quantity}</td>
                      <td className="num font-medium">{money(item.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4} className="num text-2xs text-ink-secondary">Subtotal</td>
                    <td className="num">{money(order.subtotal)}</td>
                  </tr>
                  <tr>
                    <td colSpan={4} className="num text-2xs text-ink-secondary">Shipping</td>
                    <td className="num">{money(order.shipping_amount)}</td>
                  </tr>
                  <tr>
                    <td colSpan={4} className="num text-[13px] font-semibold">Total</td>
                    <td className="num text-[13px] font-semibold">{money(order.total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>

          {order.customer_note && (
            <Card title="Note from the customer">
              <p className="text-[13px] text-ink-secondary">{order.customer_note}</p>
            </Card>
          )}

          {order.admin_notes && (
            <Card title="Internal notes">
              <pre className="whitespace-pre-wrap font-sans text-[13px] text-ink-secondary">
                {order.admin_notes}
              </pre>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <Card title="Status">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <StatusBadge status={order.status} />
                <span className="text-2xs text-ink-muted">
                  Payment: {order.payment_state}
                </span>
              </div>

              {order.payment_reference && (
                <p className="text-2xs text-ink-muted">
                  PayFast reference <span className="font-mono">{order.payment_reference}</span>
                </p>
              )}

              <div>
                <label className="label" htmlFor="note">Note for this change</label>
                <input
                  id="note"
                  className="input input-sm"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Optional — kept on the order"
                />
              </div>

              <div className="space-y-2">
                {actions.map(([next, label]) => (
                  <button
                    key={next}
                    onClick={() => changeStatus(next)}
                    disabled={busy}
                    className="btn-primary w-full"
                  >
                    {busy ? <FaSpinner className="animate-spin" /> : <FaTruck className="text-[11px]" />}
                    {label}
                  </button>
                ))}

                {canCancel && (
                  <button
                    onClick={() => changeStatus('cancelled')}
                    disabled={busy}
                    className="btn-ghost w-full text-brand-red"
                  >
                    {SHOW_STOCK ? 'Cancel and return stock' : 'Cancel this order'}
                  </button>
                )}
              </div>
            </div>
          </Card>

          <Card title="Delivery">
            <div className="space-y-2.5 text-[13px]">
              <div className="flex items-start gap-2">
                <FaMapMarkerAlt className="mt-1 shrink-0 text-[11px] text-ink-muted" aria-hidden="true" />
                <div className="text-ink-secondary">
                  <div className="font-medium text-ink">
                    {order.customer_first_name} {order.customer_last_name}
                  </div>
                  <div>{order.shipping_address}</div>
                  <div>
                    {[order.shipping_city, order.shipping_province, order.shipping_postal_code]
                      .filter(Boolean)
                      .join(', ')}
                  </div>
                </div>
              </div>

              {order.customer_email && (
                <a
                  href={`mailto:${order.customer_email}`}
                  className="flex items-center gap-2 text-ink-secondary hover:text-brand-red"
                >
                  <FaEnvelope className="shrink-0 text-[11px]" aria-hidden="true" />
                  <span className="truncate">{order.customer_email}</span>
                </a>
              )}

              {order.customer_phone && (
                <a
                  href={`tel:${order.customer_phone.replace(/\s/g, '')}`}
                  className="flex items-center gap-2 text-ink-secondary hover:text-brand-red"
                >
                  <FaPhone className="shrink-0 text-[11px]" aria-hidden="true" />
                  {order.customer_phone}
                </a>
              )}

              <div className="border-t border-hairline pt-2.5">
                <label className="label" htmlFor="tracking">Tracking number</label>
                <div className="flex gap-2">
                  <input
                    id="tracking"
                    className="input input-sm flex-1"
                    value={tracking}
                    onChange={(e) => setTracking(e.target.value)}
                    placeholder="Courier waybill"
                  />
                  <button onClick={saveTracking} disabled={busy} className="btn-ghost btn-sm">
                    Save
                  </button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <Toast toast={toast} onDismiss={dismiss} />
    </div>
  )
}

export default OrderDetailPage
